import React, { useEffect, useState } from 'react';
import { Printer } from 'lucide-react';
import { getNeraca } from '../../api';

export default function Neraca() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tanggal, setTanggal] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    setLoading(true);
    getNeraca({ tanggal })
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, [tanggal]);

  const rupiah = (val) => {
    const n = Number(val);
    return isNaN(n) ? 'Rp 0' : `Rp ${n.toLocaleString('id-ID')}`;
  };

  // ===== PDF Export =====
  const generatePDF = () => {
    if (!data) return;
    const { jsPDF } = require('jspdf');
    const autoTable = require('jspdf-autotable').default || require('jspdf-autotable');
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - 2 * margin;

    const primary = [17, 205, 239];
    const primaryDark = [0, 150, 180];
    const dark = [23, 43, 77];
    const gray = [136, 152, 170];
    const lightGray = [248, 249, 252];
    const white = [255, 255, 255];

    const balanceOk = Math.abs(data.total_aset - (data.total_kewajiban + data.total_modal)) < 1;

    // ===== HEADER =====
    doc.setFillColor(...primary);
    doc.rect(0, 0, pageW, 6, 'F');
    doc.setFillColor(...primaryDark);
    doc.rect(0, 6, pageW, 2, 'F');

    // Logo box
    doc.setFillColor(...dark);
    doc.roundedRect(margin, 16, 50, 30, 4, 4, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('L Synexa', margin + 25, 30, { align: 'center' });
    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.text('Sistem Informasi Armada', margin + 25, 37, { align: 'center' });

    // Title
    doc.setTextColor(...dark);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('NERACA', margin + 58, 25);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text('Laporan Posisi Keuangan', margin + 58, 33);
    doc.text(`Per Tanggal: ${tanggal}`, margin + 58, 40);

    // Decorative line
    doc.setDrawColor(...primary);
    doc.setLineWidth(1.5);
    doc.line(margin, 52, pageW - margin, 52);
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, 54, pageW - margin, 54);

    // Info box
    let y = 60;
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 14, 3, 3, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, y, contentW, 14, 3, 3, 'S');

    doc.setTextColor(...dark);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text('INFORMASI LAPORAN', margin + 8, y + 6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.setFontSize(7);
    const now = new Date();
    doc.text(`Dicetak: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin + 8, y + 11);
    doc.text(`Status: ${balanceOk ? 'Balance ✓' : 'Selisih: Rp ' + Math.abs(data.total_aset - (data.total_kewajiban + data.total_modal)).toLocaleString('id-ID')}`, margin + contentW / 2, y + 11);

    y += 22;

    // ===== ASET TABLE =====
    doc.setFillColor(...dark);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('ASET', margin + 6, y + 7);
    doc.setFontSize(9);
    doc.text(`Rp ${(data.total_aset || 0).toLocaleString('id-ID')}`, pageW - margin - 6, y + 7, { align: 'right' });
    y += 12;

    if (data.aset && data.aset.length > 0) {
      const asetRows = data.aset.map(a => [
        `${a.kode_akun} - ${a.nama_akun}`,
        (a.saldo || 0).toLocaleString('id-ID'),
      ]);
      asetRows.push(['TOTAL ASET', (data.total_aset || 0).toLocaleString('id-ID')]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Kode & Nama Akun', 'Saldo (Rp)']],
        body: asetRows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [0, 150, 180], textColor: [...white], fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [245, 248, 252] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
        didParseCell: function(td) {
          if (td.row.index === asetRows.length - 1) {
            td.cell.styles.fillColor = [240, 253, 244];
            td.cell.styles.textColor = [...dark];
            td.cell.styles.fontStyle = 'bold';
            td.cell.styles.lineWidth = 0.8;
            td.cell.styles.lineColor = [16, 185, 129];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // ===== KEWAJIBAN TABLE =====
    doc.setFillColor(...dark);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('KEWAJIBAN', margin + 6, y + 7);
    doc.setFontSize(9);
    doc.text(`Rp ${(data.total_kewajiban || 0).toLocaleString('id-ID')}`, pageW - margin - 6, y + 7, { align: 'right' });
    y += 12;

    if (data.kewajiban && data.kewajiban.length > 0) {
      const kwRows = data.kewajiban.map(k => [
        `${k.kode_akun} - ${k.nama_akun}`,
        (k.saldo || 0).toLocaleString('id-ID'),
      ]);
      kwRows.push(['TOTAL KEWAJIBAN', (data.total_kewajiban || 0).toLocaleString('id-ID')]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Kode & Nama Akun', 'Saldo (Rp)']],
        body: kwRows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [217, 119, 6], textColor: [...white], fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [255, 251, 235] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
        didParseCell: function(td) {
          if (td.row.index === kwRows.length - 1) {
            td.cell.styles.fillColor = [254, 243, 199];
            td.cell.styles.textColor = [...dark];
            td.cell.styles.fontStyle = 'bold';
            td.cell.styles.lineWidth = 0.8;
            td.cell.styles.lineColor = [217, 119, 6];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // Check if enough space for modal + balance check
    if (y > pageH - 80) {
      doc.addPage();
      y = margin + 10;
    }

    // ===== MODAL TABLE =====
    doc.setFillColor(...dark);
    doc.roundedRect(margin, y, contentW, 10, 2, 2, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('MODAL', margin + 6, y + 7);
    doc.setFontSize(9);
    doc.text(`Rp ${(data.total_modal || 0).toLocaleString('id-ID')}`, pageW - margin - 6, y + 7, { align: 'right' });
    y += 12;

    if (data.modal && data.modal.length > 0) {
      const mdRows = data.modal.map(m => [
        `${m.kode_akun} - ${m.nama_akun}`,
        (m.saldo || 0).toLocaleString('id-ID'),
      ]);
      mdRows.push(['TOTAL MODAL', (data.total_modal || 0).toLocaleString('id-ID')]);

      autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head: [['Kode & Nama Akun', 'Saldo (Rp)']],
        body: mdRows,
        styles: { fontSize: 8, cellPadding: 3, lineColor: [220, 220, 220], lineWidth: 0.5, textColor: [50, 50, 50] },
        headStyles: { fillColor: [16, 185, 129], textColor: [...white], fontStyle: 'bold', fontSize: 8 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        columnStyles: { 1: { halign: 'right', fontStyle: 'bold' } },
        didParseCell: function(td) {
          if (td.row.index === mdRows.length - 1) {
            td.cell.styles.fillColor = [209, 250, 229];
            td.cell.styles.textColor = [...dark];
            td.cell.styles.fontStyle = 'bold';
            td.cell.styles.lineWidth = 0.8;
            td.cell.styles.lineColor = [16, 185, 129];
          }
        },
      });
      y = doc.lastAutoTable.finalY + 12;
    }

    // Check if enough space for balance summary
    if (y > pageH - 60) {
      doc.addPage();
      y = margin + 10;
    }

    // ===== BALANCE SUMMARY =====
    doc.setFillColor(...lightGray);
    doc.roundedRect(margin, y, contentW, 32, 3, 3, 'F');
    doc.setDrawColor(230, 230, 230);
    doc.roundedRect(margin, y, contentW, 32, 3, 3, 'S');

    doc.setTextColor(...dark);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Total Kewajiban + Modal', margin + 8, y + 10);
    doc.setTextColor(...[16, 185, 129]);
    doc.text(`Rp ${(data.total_kewajiban + data.total_modal || 0).toLocaleString('id-ID')}`, pageW - margin - 8, y + 10, { align: 'right' });

    doc.setTextColor(...dark);
    doc.text('Total Aset', margin + 8, y + 20);
    doc.setTextColor(...primary);
    doc.text(`Rp ${(data.total_aset || 0).toLocaleString('id-ID')}`, pageW - margin - 8, y + 20, { align: 'right' });

    // Balance status
    const statusY = y + 26;
    if (balanceOk) {
      doc.setFillColor(209, 250, 229);
      doc.roundedRect(margin + 8, statusY - 4, contentW - 16, 8, 2, 2, 'F');
      doc.setTextColor(6, 95, 70);
    } else {
      doc.setFillColor(254, 226, 226);
      doc.roundedRect(margin + 8, statusY - 4, contentW - 16, 8, 2, 2, 'F');
      doc.setTextColor(153, 27, 27);
    }
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(
      balanceOk ? '✓ Neraca Balance (Aset = Kewajiban + Modal)' : `✗ Selisih: Rp ${Math.abs(data.total_aset - (data.total_kewajiban + data.total_modal)).toLocaleString('id-ID')}`,
      pageW / 2, statusY, { align: 'center' }
    );

    // Footer on all pages
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(...primary);
      doc.setLineWidth(1);
      doc.line(margin, pageH - 15, pageW - margin, pageH - 15);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.3);
      doc.line(margin, pageH - 14, pageW - margin, pageH - 14);

      doc.setTextColor(...gray);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text('Layanan Synexa - Sistem Informasi Armada', margin, pageH - 9);
      doc.text(`Dicetak: ${now.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}`, margin, pageH - 5);
      doc.setFont('helvetica', 'normal');
      doc.text(`Halaman ${i} dari ${pageCount}`, pageW - margin, pageH - 5, { align: 'right' });
    }

    doc.save(`Neraca_${tanggal}.pdf`);
  };

  if (loading) return <div className="page-loading">Memuat neraca...</div>;
  if (!data) return <div className="page-error">Gagal memuat data</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Neraca</h2>
        <p className="page-desc">Laporan posisi keuangan — aset, kewajiban, dan modal</p>
      </div>

      <div className="page-header-row" style={{ marginBottom: 16 }}>
        <div className="page-header-actions">
          <button className="btn btn-secondary" onClick={generatePDF}>
            <Printer size={16} /><span>Export PDF</span>
          </button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 600 }}>Per Tanggal:</label>
          <input type="date" value={tanggal} onChange={e => setTanggal(e.target.value)}
            style={{ padding: '6px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', fontSize: '0.85rem' }} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        <div className="table-wrapper">
          <h3 style={{ padding: '14px 16px', fontSize: '0.9rem', borderBottom: '2px solid var(--primary)', color: 'var(--gray-600)' }}>ASET</h3>
          <table className="data-table">
            <tbody>
              {data.aset?.map((a, i) => (
                <tr key={i}><td style={{ fontSize: '0.85rem' }}>{a.kode_akun} - {a.nama_akun}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{rupiah(a.saldo)}</td></tr>
              ))}
              <tr style={{ background: 'var(--gray-50)' }}>
                <td style={{ fontWeight: 700 }}>Total Aset</td>
                <td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{rupiah(data.total_aset)}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div>
          <div className="table-wrapper" style={{ marginBottom: 20 }}>
            <h3 style={{ padding: '14px 16px', fontSize: '0.9rem', borderBottom: '2px solid #f59e0b', color: 'var(--gray-600)' }}>KEWAJIBAN</h3>
            <table className="data-table">
              <tbody>
                {data.kewajiban?.map((k, i) => (
                  <tr key={i}><td style={{ fontSize: '0.85rem' }}>{k.kode_akun} - {k.nama_akun}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{rupiah(k.saldo)}</td></tr>
                ))}
                <tr style={{ background: 'var(--gray-50)' }}>
                  <td style={{ fontWeight: 700 }}>Total Kewajiban</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#d97706' }}>{rupiah(data.total_kewajiban)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="table-wrapper">
            <h3 style={{ padding: '14px 16px', fontSize: '0.9rem', borderBottom: '2px solid #10b981', color: 'var(--gray-600)' }}>MODAL</h3>
            <table className="data-table">
              <tbody>
                {data.modal?.map((m, i) => (
                  <tr key={i}><td style={{ fontSize: '0.85rem' }}>{m.kode_akun} - {m.nama_akun}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{rupiah(m.saldo)}</td></tr>
                ))}
                <tr style={{ background: 'var(--gray-50)' }}>
                  <td style={{ fontWeight: 700 }}>Total Modal</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#059669' }}>{rupiah(data.total_modal)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 16, padding: '14px 16px', background: '#f0fdf4', borderRadius: 'var(--radius)', border: '1px solid #bbf7d0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem' }}>
              <span>Total Kewajiban + Modal</span>
              <span style={{ color: '#059669' }}>{rupiah(data.total_kewajiban + data.total_modal)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '1rem', marginTop: 8 }}>
              <span>Total Aset</span>
              <span style={{ color: 'var(--primary)' }}>{rupiah(data.total_aset)}</span>
            </div>
            <div style={{
              marginTop: 8, padding: '8px 12px', borderRadius: 'var(--radius)',
              background: Math.abs(data.total_aset - (data.total_kewajiban + data.total_modal)) < 1 ? '#d1fae5' : '#fee2e2',
              color: Math.abs(data.total_aset - (data.total_kewajiban + data.total_modal)) < 1 ? '#065f46' : '#991b1b',
              textAlign: 'center', fontWeight: 600, fontSize: '0.9rem'
            }}>
              {Math.abs(data.total_aset - (data.total_kewajiban + data.total_modal)) < 1
                ? '✓ Neraca Balance (Aset = Kewajiban + Modal)'
                : `✗ Selisih: ${rupiah(data.total_aset - (data.total_kewajiban + data.total_modal))}`}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
